import React, { useState } from "react";
import styled from "styled-components";
import { ChevronDown, ChevronUp } from "react-feather";
import { ChangesTrigger, ClickOutside } from "@cinesys/ui/components";
import SpotterChangesPanel, {
  SpotterChangesPanelProps,
} from "./SpotterChangesPanel";
import { SPOTTER_STRINGS } from "./strings";

/** Matches `FieldsScreen`/`FrScreen`/`RenameScreen`'s own `Aside` — the same
 *  `PageWrapperTwoColumn` row-vs-stacked breakpoint (its default `minWidth`, unchanged by any
 *  call site). */
const DESKTOP_MIN_WIDTH = "1030px";

/**
 * Round 14: a compact "N unsaved changes" badge in `SpotterHeader`'s own tab row, right-aligned —
 * `Header` is ALREADY sticky (below `ShellHeader`, see its own doc comment), so this stays
 * visible at every scroll position, unlike the full `SpotterChangesPanel` sitting in a page's own
 * `<Aside>` (sticky only within its own column, and on a narrow/stacked mobile layout `Aside`
 * falls to the very bottom of the page in
 * normal flow — see those files' `Aside` doc comments).
 *
 * Two behaviors depending on viewport, both pure-CSS (matching `Aside`'s own breakpoint, no JS
 * resize listener needed):
 *   - Below the desktop breakpoint (stacked layout, `Aside` below `Main`): the badge is a real
 *     clickable trigger — the sidebar isn't reliably in view, so clicking it opens the SAME
 *     `SpotterChangesPanel` card (unmodified) as a right-aligned dropdown, giving mobile a fast path
 *     to review/save without scrolling to the bottom of the page.
 *   - At/above the desktop breakpoint (side-by-side layout): `SpotterChangesPanel` already sits at
 *     the top of the visible right column, so the header badge is inert here — no chevron, no
 *     click, no dropdown (`pointer-events: none` + the dropdown itself force-hidden by media
 *     query) — it's a plain "you have unsaved changes" status readout, not a second way to reach
 *     the same panel that's already on screen.
 *
 * Renders nothing at all when there's nothing to report (no pending changes, no just-saved
 * confirmation, no error) — an empty pill sitting in the header permanently would be clutter,
 * whereas the sidebar `SpotterChangesPanel` always has a home for that state regardless. Tone follows
 * state: amber while changes are pending, red on a save error, green for the brief post-save
 * "Saved." confirmation (`count === 0 && saveSuccess`).
 */
const Positioner = styled(ClickOutside)`
  position: relative;
  margin-left: auto;
`;

/** Shared pill (`@cinesys/ui`'s `ChangesTrigger`) rendered as a real `<button>` here — this one
 *  is clickable (opens `Dropdown`) below the desktop breakpoint. Its `pending`/`success`/`error`
 *  tones read the shared `--orangeChipBackground`/`--greenBackground`/`--redBackground` (etc.)
 *  tokens, which `SpotterHeader`'s own CSS-var overrides re-skin to the `--spotter*` amber/ok/danger
 *  palette (see that file) — `Header`'s green/red tones already match by construction (Spotter's
 *  own nflOkSoft/nflDangerSoft ARE the shared green/red tokens' values, not just similar to them). */
const Trigger = styled(ChangesTrigger).attrs({ as: "button" })`
  cursor: pointer;

  /* At the desktop breakpoint \`SpotterChangesPanel\` is already visible atop the right column — this
   * badge becomes a plain status readout, not a second, redundant way to open it. */
  @media (min-width: ${DESKTOP_MIN_WIDTH}) {
    cursor: default;
    pointer-events: none;
  }
`;

const ChevronWrap = styled.span`
  display: inline-flex;

  @media (min-width: ${DESKTOP_MIN_WIDTH}) {
    display: none;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 360px;
  max-width: calc(100vw - 44px);
  z-index: 40;

  @media (min-width: ${DESKTOP_MIN_WIDTH}) {
    display: none;
  }
`;

const SpotterHeaderChanges = (props: SpotterChangesPanelProps) => {
  const { groups, saveError, saveSuccess } = props;
  const [open, setOpen] = useState(false);
  const count = groups.reduce((n, g) => n + g.items.length, 0);

  if (count === 0 && !saveError && !saveSuccess) {
    return null;
  }

  const tone = saveError ? "error" : count > 0 ? "pending" : "success";

  return (
    <Positioner onClickOutside={() => setOpen(false)}>
      <Trigger type="button" $tone={tone} onClick={() => setOpen((o) => !o)}>
        {count > 0
          ? SPOTTER_STRINGS.changesPanel.unsavedChangeCount(count)
          : saveError
          ? SPOTTER_STRINGS.changesPanel.title
          : SPOTTER_STRINGS.changesPanel.saved}
        <ChevronWrap>
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </ChevronWrap>
      </Trigger>
      {open && (
        <Dropdown>
          <SpotterChangesPanel {...props} />
        </Dropdown>
      )}
    </Positioner>
  );
};

export default SpotterHeaderChanges;
